import { useState } from 'react';
import { Check, ExternalLink } from 'lucide-react';

import { HighlightedSelections } from '@/components/HighlightedSelections';
import type { FeedItem } from '@/services';
import { cn } from '@/services/utils';
import { timeAgo } from '@/utils/timeAgo';

export function OutletLogo({
  item,
  compact = false,
  tiny = false,
}: {
  item: FeedItem;
  compact?: boolean;
  /** Inline icon size for dense rows (e.g. next to the outlet name). */
  tiny?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const outletName = item.siteName ?? item.articleDomain;
  const sizeClass = tiny ? 'size-[18px]' : compact ? 'size-8' : 'size-10';

  if (!item.logoUrl || failed) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full bg-[#e7f0e8] font-bold uppercase text-[#496653]',
          sizeClass,
          tiny ? 'text-[10px]' : compact ? 'text-xs' : 'text-sm'
        )}
      >
        {(outletName.trim()[0] ?? '?').toUpperCase()}
      </span>
    );
  }

  return (
    <img
      src={item.logoUrl}
      alt=""
      className={cn('shrink-0 rounded-full border border-slate-200 object-cover', sizeClass)}
      onError={() => setFailed(true)}
    />
  );
}

export function FeedCard({ item }: { item: FeedItem }) {
  const highlights = (item.matchedTokens ?? [])
    .map((span) => ({ start: span.start, length: span.end - span.start }))
    .sort((left, right) => left.start - right.start);
  const outletName = item.siteName ?? item.articleDomain;
  const articleLabel = item.articleTitle ?? item.articleDomain;
  const articleTitle = `${articleLabel}\nMatched ${timeAgo(item.createdAtUtc)}`;
  const publishedTitle = item.publishedAtUtc?.toLocaleDateString(undefined, { dateStyle: 'long' });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <OutletLogo item={item} compact />
        <div className="min-w-0 flex-1">
          <a
            href={item.articleUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={articleTitle}
            className="group/source flex max-w-full items-center gap-1 rounded-sm text-xs font-semibold text-slate-900 outline-none hover:text-[#2f4938] hover:underline focus-visible:ring-2 focus-visible:ring-[#496653] focus-visible:ring-offset-1"
          >
            <span className="truncate">{outletName}</span>
            <span className="sr-only"> (opens article in a new tab)</span>
            <ExternalLink
              className="size-3 shrink-0 text-slate-400 group-hover/source:text-[#496653]"
              aria-hidden="true"
            />
          </a>
          {item.publishedAtUtc && (
            <p className="text-xs text-slate-500" title={publishedTitle}>
              {timeAgo(item.publishedAtUtc)}
            </p>
          )}
        </div>
        {item.senseConfirmed && (
          <span
            role="img"
            aria-label="Same meaning as this card"
            title="Same meaning as this card"
            className="flex size-5 shrink-0 items-center justify-center rounded-full bg-[#00C1B5] text-white"
          >
            <Check className="size-3" strokeWidth={3} aria-hidden="true" />
          </span>
        )}
      </div>

      <blockquote className="border-l-2 border-amber-200 pl-3 text-[15px] leading-relaxed text-slate-800">
        <HighlightedSelections text={item.sentence} selections={highlights} />
      </blockquote>
    </div>
  );
}
